/* eslint-disable @typescript-eslint/no-unsafe-function-type */

"use client";

import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Copy, Share2 } from "lucide-react";
import { toast } from "sonner";
import { AnimatePresence, motion } from "framer-motion";
import { cn } from "@/lib/utils";
import {
  IconBrandFacebookFilled,
  IconBrandLinkedinFilled,
  IconBrandMedium,
  IconBrandPinterestFilled,
  IconBrandReddit,
  IconBrandTelegram,
  IconBrandWhatsapp,
  IconBrandWhatsappFilled,
  IconBrandXFilled,
  IconMailFilled,
  IconPrinter,
} from "@tabler/icons-react";
import { useIsMobile } from "@/hooks/use-mobile";

const shareLinks = [
  {
    label: "Facebook",
    icon: IconBrandFacebookFilled,
    color: "#4267B2",
    onClick: (url: string, title: string) => {
      window.open(
        `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
          url
        )}&t=${encodeURIComponent(title)}`,
        "_blank"
      );
    },
  },
  {
    label: "X",
    icon: IconBrandXFilled,
    color: "#000",
    onClick: (url: string, title: string) => {
      window.open(
        `https://twitter.com/intent/tweet?text=${encodeURIComponent(
          title
        )}&url=${encodeURIComponent(url)}`,
        "_blank"
      );
    },
  },
  {
    label: "WhatsApp",
    icon: IconBrandWhatsapp,
    color: "#25D366",
    onClick: (url: string, title: string) => {
      window.open(
        `https://wa.me/?text=${encodeURIComponent(`${title} ${url}`)}`,
        "_blank"
      );
    },
  },
  {
    label: "LinkedIn",
    icon: IconBrandLinkedinFilled,
    color: "#0077B5",
    onClick: (url: string) => {
      window.open(
        `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
          url
        )}`,
        "_blank"
      );
    },
  },
  {
    label: "Reddit",
    icon: IconBrandReddit,
    color: "#FF5700",
    onClick: (url: string, title: string) => {
      window.open(
        `https://www.reddit.com/submit?url=${encodeURIComponent(
          url
        )}&title=${encodeURIComponent(title)}`,
        "_blank"
      );
    },
  },
  {
    label: "Medium",
    icon: IconBrandMedium,
    color: "#000",
    onClick: (url: string) => {
      window.open(
        `https://medium.com/m/global-identity?redirectUrl=${encodeURIComponent(
          url
        )}`,
        "_blank"
      );
    },
  },
  {
    label: "Pinterest",
    icon: IconBrandPinterestFilled,
    color: "#E60023",
    onClick: (url: string, title: string, featuredImage: string) => {
      window.open(
        `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(
          url
        )}&description=${encodeURIComponent(title)}&media=${encodeURIComponent(
          featuredImage || ""
        )}`,
        "_blank"
      );
    },
  },
  {
    label: "Telegram",
    icon: IconBrandTelegram,
    color: "#0088cc",
    onClick: (url: string, title: string) => {
      window.open(
        `https://t.me/share/url?url=${encodeURIComponent(
          url
        )}&text=${encodeURIComponent(title)}`,
        "_blank"
      );
    },
  },
  {
    label: "Email",
    icon: IconMailFilled,
    color: "#333",
    onClick: (url: string, title: string) => {
      window.open(
        `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(
          url
        )}`,
        "_blank"
      );
    },
  },
  {
    label: "Print",
    icon: IconPrinter,
    color: "#6c757d",
    onClick: () => {
      window.print();
    },
  },
];

export function CollapsibleShare({
  title,
  description,
  url,
  featuredImage,
}: {
  title: string;
  description?: string;
  url?: string;
  featuredImage: string
}) {
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile()
  const containerRef = useRef<HTMLDivElement>(null);
  const [shareData, setShareData] = useState({
    title: "",
    description: "",
    url: "",
    featuredImage: "",
  });

  const baseUrl =
    process.env.NEXT_PUBLIC_FRONTEND_URL || "https://moneyoval.com";

  const imageUrl = process.env.NEXT_PUBLIC_MEDIA_BASE_URL

  // on click share button, scroll to the share button - only for mobile 
  useEffect(() => {
    if (open && isMobile) {
      const shareButton = document.getElementById("mo-article-details-header");
      if (shareButton) {
        shareButton.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [open, isMobile]);

  useEffect(() => {
    setShareData({
      title: title || document.title,
      description: description || "",
      url: baseUrl + url || window.location.href,
      featuredImage: imageUrl + featuredImage
    });
  }, [title, description, url, baseUrl, featuredImage, imageUrl]);

  // Handle click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // const handleShareClick = (shareAction: Function) => {
  //   if (shareAction.length === 3) {
  //     shareAction(shareData.title, shareData.url, shareData.description, shareData.featuredImage);
  //   } else if (shareAction.length === 2) {
  //     shareAction(shareData.title, shareData.url);
  //   } else {
  //     shareAction(shareData.url);
  //   }
  //   setOpen(false);
  // };

  const handleShareClick = (
    shareAction: (url: string, title: string, featuredImage: string) => void
  ) => {
    shareAction(shareData.url, shareData.title, shareData.featuredImage!);
  };

  return (
    <TooltipProvider delayDuration={200}>
      <div ref={containerRef} className="relative z-50">
        {/* Main Share Button */}
        <Button
          size="icon"
          variant="ghost"
          className={cn(
            "relative rounded-full transition-all",
            "bg-white dark:bg-neutral-800",
            "border border-gray-200 dark:border-neutral-700",
            "shadow-sm hover:shadow-md",
            "hover:bg-gray-50 dark:hover:bg-neutral-700",
            open ? "ring-2 ring-primary/50" : ""
          )}
          aria-label="Share options"
          onClick={() => setOpen(!open)}
        >
          <Share2
            size={25}
            className={cn(
              "transition-transform duration-300",
              open ? "rotate-45 scale-110" : ""
            )}
          />
        </Button>

        {/* Share Options */}
        <AnimatePresence>
          {open && (
            <motion.div
              id="mo-share-button-cs"
              className={cn(
                "absolute left-1/2 -translate-x-1/2 top-full mt-2",
                "flex p-2",
                "bg-white dark:bg-neutral-800",
                "rounded-lg shadow-xl",
                "border border-gray-200 dark:border-neutral-700",
                "max-w-[90vw]",
                // Vertical layout for mobile and tablet (sm breakpoint and below)
                "flex-col items-center gap-2 sm:flex-row sm:items-center sm:overflow-x-auto"
              )}
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.95 }}
              transition={{ type: "spring", stiffness: 400, damping: 20 }}
            >
              {shareLinks.map(
                ({ label, icon: Icon, color, onClick }) => (
                  <Tooltip key={label}>
                    <TooltipTrigger asChild>
                      <Button
                        size="icon"
                        variant="ghost"
                        className={cn(
                          "rounded-full transition-all",
                          "w-10 h-10 sm:w-10 sm:h-10",
                          "hover:scale-105"
                        )}
                        style={{ color }}
                        aria-label={`Share on ${label}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleShareClick(onClick);
                        }}
                      >
                        <Icon size={25} className="shrink-0" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="text-xs">
                      {label}
                    </TooltipContent>
                  </Tooltip>
                )
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </TooltipProvider>
  );
}
